'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, use, useState, useRef } from 'react';
import { useWebHaptics } from 'web-haptics/react';
import Image from 'next/image';
import { categoryConfig } from '@/data/closet';
import { getSizeOptions } from '@/lib/closet/size-options';
import { useCloset } from '@/contexts/ClosetContext';
import type { ClothingCategory, Season, ClothingSubcategory } from '@/types/closet';
import type { ScrapedProduct } from '@/lib/closet/product-scraper';
import { ModalScrim } from '@/components/closet/ModalScrim';
import { closetAuthHeaders, readClosetAuthToken } from '@/lib/closet/client-auth';
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

// Pure drag handler (no component state) — hoisted so it isn't rebuilt per render.
const handleDragOver = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

// Subcategory options per category — pure lookup, hoisted out of the component.
const getSubcategoryOptions = (category: ClothingCategory): ClothingSubcategory[keyof ClothingSubcategory][] | null => {
  const subcategoryMap: Partial<Record<ClothingCategory, ClothingSubcategory[keyof ClothingSubcategory][]>> = {
    accessories: ['sunglasses', 'tie', 'belt', 'scarf', 'gloves', 'bag', 'watch', 'socks', 'wallet', 'tech', 'jewelry'],
    footwear: ['sneakers', 'boots', 'shoes', 'sandals', 'slippers'],
    headwear: ['cap', 'beanie', 'hat', 'headband']
  };
  return subcategoryMap[category] || null;
};

// Hoisted to module scope so the `throw`s stay out of the component body — the
// React Compiler bails on a ThrowStatement inside try/catch and would skip
// memoizing the modal. These are plain async helpers the compiler never inspects.
async function uploadClosetImage(file: File): Promise<string> {
  const uploadFormData = new FormData();
  uploadFormData.append('file', file);
  const uploadResponse = await fetch('/api/closet/upload', {
    method: 'POST',
    // No Content-Type — the browser sets the multipart boundary itself.
    headers: closetAuthHeaders(),
    body: uploadFormData
  });
  if (!uploadResponse.ok) {
    throw new Error('Failed to upload image');
  }
  const {
    url
  } = await uploadResponse.json();
  return url;
}
async function createClosetItem(body: Record<string, unknown>) {
  const itemResponse = await fetch('/api/closet/items', {
    method: 'POST',
    headers: closetAuthHeaders({
      'Content-Type': 'application/json'
    }),
    body: JSON.stringify(body)
  });
  if (!itemResponse.ok) {
    throw new Error('Failed to create item');
  }
  const {
    item
  } = await itemResponse.json();
  return item;
}
async function importClosetItemFromUrl(body: Record<string, unknown>) {
  const response = await fetch('/api/closet/import-url', {
    method: 'POST',
    headers: closetAuthHeaders({
      'Content-Type': 'application/json'
    }),
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error('Failed to import item');
  }
  const {
    item
  } = await response.json();
  return item;
}
interface AddFromUrlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}
export function AddFromUrlModal(props) {
  const $ = _c(2);
  if (!props.isOpen) {
    return null;
  }
  let t0;
  if ($[0] !== props) {
    t0 = <AddFromUrlModalMounted {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function AddFromUrlModalMounted(props) {
  const $ = _c(5);
  let t0;
  if ($[0] !== props) {
    t0 = {
      ...props
    };
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const modal = useAddFromUrlModalController(t0);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AddFromUrlModalDialog />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== modal) {
    t2 = <AddFromUrlModalContext.Provider value={modal}>{t1}</AddFromUrlModalContext.Provider>;
    $[3] = modal;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function useAddFromUrlModalController({
  onClose,
  onSuccess
}: AddFromUrlModalProps) {
  const haptic = useWebHaptics();
  const {
    addItem,
    updateItem
  } = useCloset();
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [processingImages, setProcessingImages] = useState(false);
  const [error, setError] = useState('');
  const [scrapedImages, setScrapedImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [mode, setMode] = useState<'url' | 'upload'>('url');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [processingUpload, setProcessingUpload] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    url: '',
    name: '',
    category: '' as ClothingCategory | '',
    subcategory: '' as ClothingSubcategory[keyof ClothingSubcategory] | '',
    brand: '',
    size: '',
    color: '',
    tags: '',
    price: '',
    season: 'all' as Season
  });

  // Fetch preview data when URL changes
  const handleUrlBlur = async () => {
    if (!formData.url || formData.url === '') return;
    setScraping(true);
    setProcessingImages(true);
    setError('');
    try {
      const response = await fetch('/api/closet/import-url', {
        method: 'POST',
        headers: closetAuthHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          url: formData.url,
          preview: true
        })
      });

      // Without the status check, a 401/500 error body falls through to the
      // generic "couldn't scrape" message and hides the real reason.
      if (!response.ok) {
        throw new Error(`Preview failed: ${response.status}`);
      }
      const result = await response.json();
      if (result.success && result.data) {
        const data = result.data as ScrapedProduct;

        // Update form with scraped data
        setFormData(prev => ({
          ...prev,
          name: data.title || prev.name,
          brand: data.brand || prev.brand,
          price: data.price ? String(data.price) : prev.price
        }));

        // Set processed images (now they're blob URLs)
        if (data.images && data.images.length > 0) {
          setScrapedImages(data.images);
          setSelectedImageIndex(0);
        } else {
          // No images found, switch to upload mode with delay for animation
          setTimeout(() => {
            setMode('upload');
            setError('NO IMAGES FOUND. PLEASE UPLOAD AN IMAGE MANUALLY.');
          }, 300);
        }
      } else {
        // Failed to fetch product data, switch to upload mode with delay
        setTimeout(() => {
          setMode('upload');
          setError('UNABLE TO FETCH PRODUCT IMAGES. PLEASE UPLOAD MANUALLY.');
        }, 300);
      }
    } catch {
      // Network or parsing error, switch to upload mode with delay
      setTimeout(() => {
        setMode('upload');
        setError('FAILED TO LOAD PRODUCT. PLEASE UPLOAD AN IMAGE MANUALLY.');
      }, 300);
    }
    // No `finally`: the React Compiler bails on try/finally. The catch above
    // swallows and nothing returns early, so this always runs.
    setScraping(false);
    setProcessingImages(false);
  };
  const handleFileSelect = (file: File) => {
    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      setError('INVALID FILE TYPE. ONLY JPEG, PNG, AND WEBP IMAGES ARE ALLOWED.');
      return;
    }
    setUploadedFile(() => file);
    setError('');
    setProcessingUpload(true);

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedImageUrl(reader.result as string);
      // Note: Background removal will happen on form submit
      setProcessingUpload(false);
    };
    reader.readAsDataURL(file);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file_0 = e.target.files?.[0];
    if (file_0) {
      handleFileSelect(file_0);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e_0: React.DragEvent) => {
    e_0.preventDefault();
    e_0.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e_1: React.DragEvent) => {
    e_1.preventDefault();
    e_1.stopPropagation();
    if (e_1.currentTarget === e_1.target) {
      setIsDragging(false);
    }
  };
  const handleDrop = (e_2: React.DragEvent) => {
    e_2.preventDefault();
    e_2.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e_2.dataTransfer.files);
    const imageFile = files.find(file_1 => VALID_IMAGE_TYPES.includes(file_1.type));
    if (imageFile) {
      handleFileSelect(imageFile);
    } else if (files.length > 0) {
      setError('PLEASE DROP AN IMAGE FILE (JPEG, PNG, OR WEBP)');
    }
  };
  const handleSubmit = async (e_3: React.FormEvent) => {
    e_3.preventDefault();
    setError('');
    if (mode === 'url' && !formData.url) {
      setError('URL IS REQUIRED');
      return;
    }
    if (mode === 'upload' && !uploadedFile) {
      setError('PLEASE SELECT AN IMAGE FILE');
      return;
    }
    if (!formData.name || !formData.category) {
      setError('NAME AND CATEGORY ARE REQUIRED');
      return;
    }

    // Check if subcategory is required for accessories and footwear
    if ((formData.category === 'accessories' || formData.category === 'footwear') && !formData.subcategory) {
      setError('SUBCATEGORY IS REQUIRED FOR ACCESSORIES AND FOOTWEAR');
      return;
    }
    setLoading(true);
    try {
      if (!readClosetAuthToken()) {
        setError('AUTHENTICATION REQUIRED');
        setLoading(false);
        return;
      }

      // Create the item with optimistic update
      const tempId = `temp-${Date.now()}`;
      const newItem = {
        id: tempId,
        name: formData.name,
        category: formData.category,
        brand: formData.brand || undefined,
        size: formData.size || undefined,
        color: formData.color || undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        image: mode === 'upload' ? uploadedImageUrl! : scrapedImages[selectedImageIndex] || '',
        isProcessing: true,
        createdAt: new Date()
      };

      // Add to context immediately
      addItem(newItem);

      // Close modal and call success callback
      haptic.trigger('success');
      onClose();
      if (onSuccess) onSuccess();

      // Continue processing in background
      if (mode === 'upload' && uploadedFile) {
        processUploadInBackground(tempId, uploadedFile, newItem);
      } else {
        processUrlImportInBackground(tempId, newItem);
      }
    } catch (error_0) {
      haptic.trigger('error');
      if (error_0 instanceof Error) {
        setError(error_0.message.toUpperCase());
      } else {
        setError('FAILED TO ADD ITEM. PLEASE TRY AGAIN.');
      }
      setLoading(false);
    }
  };
  const processUploadInBackground = async (tempId_0: string, file_2: File, itemData: any) => {
    try {
      // First upload the image, then create the item with the uploaded URL.
      const uploadedUrl = await uploadClosetImage(file_2);
      const item = await createClosetItem({
        ...itemData,
        imageUrl: uploadedUrl,
        url: formData.url || undefined,
        subcategory: formData.subcategory || undefined
      });

      // Update the item in context with real data
      updateItem(tempId_0, {
        ...item,
        isProcessing: false
      });
    } catch {
      // Update item to show error
      updateItem(tempId_0, {
        isProcessing: false,
        error: 'Failed to process item'
      });
    }
  };
  const processUrlImportInBackground = async (tempId_1: string, itemData_0: any) => {
    try {
      const item_0 = await importClosetItemFromUrl({
        url: formData.url,
        name: itemData_0.name,
        category: itemData_0.category,
        subcategory: itemData_0.subcategory,
        brand: itemData_0.brand,
        size: itemData_0.size,
        color: itemData_0.color,
        tags: itemData_0.tags,
        price: itemData_0.price,
        season: itemData_0.season,
        imageUrl: itemData_0.image
      });

      // Update the item in context with real data
      updateItem(tempId_1, {
        ...item_0,
        isProcessing: false
      });
    } catch {
      // Update item to show error
      updateItem(tempId_1, {
        isProcessing: false,
        error: 'Failed to process item'
      });
    }
  };
  const sizeOptions = formData.category ? getSizeOptions(formData.category) : null;

  // Changing the category resets size to that category's default. This is event
  // logic (it should only run on a category change), so it lives in the handler
  // rather than an effect that re-fires on render.
  const handleCategoryChange = (category: ClothingCategory | '') => {
    const nextSizeOptions = category ? getSizeOptions(category) : null;
    setFormData(prev_0 => ({
      ...prev_0,
      category,
      subcategory: '',
      size: nextSizeOptions ? nextSizeOptions.defaultValue : prev_0.size
    }));
  };
  return {
    haptic,
    onClose,
    loading,
    scraping,
    processingImages,
    error,
    scrapedImages,
    selectedImageIndex,
    mode,
    uploadedFile,
    uploadedImageUrl,
    processingUpload,
    isDragging,
    fileInputRef,
    formData,
    sizeOptions,
    setMode,
    setError,
    setSelectedImageIndex,
    setFormData,
    handleCategoryChange,
    handleUrlBlur,
    handleFileChange,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleSubmit,
    getSubcategoryOptions
  };
}
type AddFromUrlModalController = ReturnType<typeof useAddFromUrlModalController>;
const AddFromUrlModalContext = createContext<AddFromUrlModalController | null>(null);
function useAddFromUrlModal() {
  const modal = use(AddFromUrlModalContext);
  if (!modal) {
    throw new Error('useAddFromUrlModal must be used within AddFromUrlModal');
  }
  return modal;
}
function AddFromUrlModalDialog() {
  const $ = _c(7);
  const {
    onClose
  } = useAddFromUrlModal();
  let t0;
  if ($[0] !== onClose) {
    t0 = <button type="button" onClick={onClose} className="absolute top-4 right-4 text-xs opacity-40 hover:opacity-100">CLOSE</button>;
    $[0] = onClose;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2 className="mb-6 text-sm">ADD TO CLOSET</h2>;
    t2 = <ModeToggle />;
    t3 = <AddFromUrlForm />;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t0) {
    t4 = <ModalScrim><div className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto bg-white p-6 dark:bg-neutral-950">{t0}{t1}{t2}{t3}</div></ModalScrim>;
    $[5] = t0;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function ModeToggle() {
  const $ = _c(17);
  const {
    haptic,
    mode,
    setMode,
    setError
  } = useAddFromUrlModal();
  let t0;
  if ($[0] !== haptic || $[1] !== setError || $[2] !== setMode) {
    t0 = nextMode => {
      haptic.trigger("selection");
      setMode(nextMode);
      setError("");
    };
    $[0] = haptic;
    $[1] = setError;
    $[2] = setMode;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const chooseMode = t0;
  let t1;
  if ($[4] !== chooseMode) {
    t1 = () => chooseMode("url");
    $[4] = chooseMode;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = `flex-1 px-3 py-2 text-xs transition-colors ${mode === "url" ? "border-b-2 border-zinc-900 dark:border-white" : "border-b border-zinc-300 dark:border-zinc-700"}`;
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <button type="button" onClick={t1} className={t2}>IMPORT FROM URL</button>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== chooseMode) {
    t4 = () => chooseMode("upload");
    $[9] = chooseMode;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = `flex-1 px-3 py-2 text-xs transition-colors ${mode === "upload" ? "border-b-2 border-zinc-900 dark:border-white" : "border-b border-zinc-300 dark:border-zinc-700"}`;
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <button type="button" onClick={t4} className={t5}>UPLOAD IMAGE</button>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = <div className="mb-6 flex gap-2">{t3}{t6}</div>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function AddFromUrlForm() {
  const $ = _c(6);
  const {
    handleSubmit
  } = useAddFromUrlModal();
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SourceInput />;
    t1 = <ImagePreview />;
    t2 = <ProductDetailsFields />;
    t3 = <SubmitActions />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== handleSubmit) {
    t4 = <form onSubmit={handleSubmit} className="space-y-4">{t0}{t1}{t2}{t3}</form>;
    $[4] = handleSubmit;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function SourceInput() {
  const $ = _c(2);
  const modal = useAddFromUrlModal();
  if (modal.mode === "url") {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <UrlInput />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <UploadInput />;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function UrlInput() {
  const $ = _c(12);
  const {
    formData,
    setFormData,
    handleUrlBlur,
    scraping,
    processingImages
  } = useAddFromUrlModal();
  const t0 = formData.url || "";
  let t1;
  if ($[0] !== setFormData) {
    t1 = e => setFormData(prev => ({
      ...prev,
      url: e.target.value
    }));
    $[0] = setFormData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== handleUrlBlur || $[3] !== t0 || $[4] !== t1) {
    t2 = <input type="url" placeholder="PRODUCT URL" aria-label="Product URL" value={t0} onChange={t1} onBlur={handleUrlBlur} className="w-full border-b border-zinc-300 bg-transparent px-2 py-1 text-xs placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:placeholder:text-zinc-600 dark:focus:border-white" required={true} />;
    $[2] = handleUrlBlur;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== processingImages || $[7] !== scraping) {
    t3 = scraping && <p className="mt-1 text-xs text-zinc-500">{processingImages ? "PROCESSING IMAGES..." : "FETCHING PRODUCT INFO..."}</p>;
    $[6] = processingImages;
    $[7] = scraping;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <div key="url-mode">{t2}{t3}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function UploadInput() {
  const modal = useAddFromUrlModal();
  return <div key="upload-mode">
      <input ref={modal.fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={modal.handleFileChange} aria-label="Upload clothing image" className="hidden" />
      <div onClick={() => modal.fileInputRef.current?.click()} onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        modal.fileInputRef.current?.click();
      }
    }} onDragOver={modal.handleDragOver} onDragEnter={modal.handleDragEnter} onDragLeave={modal.handleDragLeave} onDrop={modal.handleDrop} role="button" tabIndex={0} className={`w-full cursor-pointer border border-dashed px-4 py-8 text-xs transition-colors duration-200 ${modal.isDragging ? 'border-zinc-900 bg-zinc-50 dark:border-white dark:bg-zinc-900' : 'border-zinc-300 hover:border-zinc-900 dark:border-zinc-700 dark:hover:border-white'}`}>
        {modal.processingUpload ? 'PROCESSING IMAGE...' : modal.uploadedFile ? modal.uploadedFile.name : modal.isDragging ? 'DROP IMAGE HERE' : 'CLICK TO SELECT IMAGE OR DRAG & DROP'}
      </div>
    </div>;
}
function ImagePreview() {
  const $ = _c(9);
  const modal = useAddFromUrlModal();
  if (modal.mode === "url" && modal.processingImages && modal.scrapedImages.length === 0) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <div className="space-y-2"><div className="mx-auto flex aspect-square w-full max-w-xs animate-pulse items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-900"><p className="text-xs text-zinc-500">REMOVING BACKGROUND…</p></div></div>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (modal.mode === "upload" && modal.uploadedImageUrl) {
    let t0;
    if ($[1] !== modal.uploadedImageUrl) {
      t0 = <Image src={modal.uploadedImageUrl} alt="Uploaded item" fill={true} className="object-contain p-4" sizes="(max-width: 640px) 100vw, 384px" />;
      $[1] = modal.uploadedImageUrl;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    let t1;
    if ($[3] !== modal.processingUpload) {
      t1 = modal.processingUpload && <div className="absolute inset-0 flex items-center justify-center bg-neutral-950/50"><p className="text-xs text-white">PROCESSING…</p></div>;
      $[3] = modal.processingUpload;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== t0 || $[6] !== t1) {
      t2 = <div className="space-y-2"><div className="relative mx-auto aspect-square w-full max-w-xs overflow-hidden rounded-lg bg-zinc-100 dark:bg-zinc-900">{t0}{t1}</div></div>;
      $[5] = t0;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  if (modal.mode === "url" && modal.scrapedImages.length > 0) {
    let t0;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <ScrapedImagePicker />;
      $[8] = t0;
    } else {
      t0 = $[8];
    }
    return t0;
  }
  return null;
}
function ScrapedImagePicker() {
  const $ = _c(9);
  const {
    scrapedImages,
    selectedImageIndex,
    setSelectedImageIndex
  } = useAddFromUrlModal();
  const selectedImage = scrapedImages[selectedImageIndex];
  if (!selectedImage) {
    return null;
  }
  let t0;
  if ($[0] !== selectedImage) {
    t0 = <div className="relative mx-auto aspect-square w-full max-w-xs overflow-hidden rounded-lg bg-zinc-100 dark:bg-zinc-900"><Image src={selectedImage} alt="Product" fill={true} className="object-contain p-4" sizes="(max-width: 640px) 100vw, 384px" /></div>;
    $[0] = selectedImage;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== scrapedImages || $[3] !== selectedImageIndex || $[4] !== setSelectedImageIndex) {
    t1 = scrapedImages.length > 1 && <div className="flex justify-center gap-2 overflow-x-auto">{scrapedImages.map((img, index) => <button key={img} type="button" onClick={() => setSelectedImageIndex(index)} className={`relative size-12 flex-shrink-0 border ${selectedImageIndex === index ? "border-zinc-900 dark:border-white" : "border-zinc-300 dark:border-zinc-700"}`}><Image src={img} alt={`Product ${index + 1}`} fill={true} className="object-cover" sizes="48px" /></button>)}</div>;
    $[2] = scrapedImages;
    $[3] = selectedImageIndex;
    $[4] = setSelectedImageIndex;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t0 || $[7] !== t1) {
    t2 = <div className="space-y-2">{t0}{t1}</div>;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function ProductDetailsFields() {
  const $ = _c(14);
  const modal = useAddFromUrlModal();
  const t0 = modal.formData.name || "";
  let t1;
  if ($[0] !== modal) {
    t1 = e => modal.setFormData(prev => ({
      ...prev,
      name: e.target.value
    }));
    $[0] = modal;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = <div><input type="text" placeholder="ITEM NAME" aria-label="Item name" value={t0} onChange={t1} className="w-full border-b border-zinc-300 bg-transparent px-2 py-1 text-xs placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:placeholder:text-zinc-600 dark:focus:border-white" required={true} /></div>;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CategoryFields />;
    t4 = <BrandSizeFields />;
    t5 = <ColorTagFields />;
    t6 = <PriceSeasonFields />;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== modal.error) {
    t7 = modal.error && <p className="text-xs text-red-600 dark:text-red-400">{modal.error}</p>;
    $[9] = modal.error;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t2 || $[12] !== t7) {
    t8 = <>{t2}{t3}{t4}{t5}{t6}{t7}</>;
    $[11] = t2;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
function CategoryFields() {
  const $ = _c(12);
  const modal = useAddFromUrlModal();
  const t0 = modal.formData.category || "";
  let t1;
  if ($[0] !== modal) {
    t1 = e => modal.handleCategoryChange(e.target.value as ClothingCategory | '');
    $[0] = modal;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <option value="">SELECT CATEGORY</option>;
    t3 = Object.entries(categoryConfig).flatMap(_temp);
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t0 || $[5] !== t1) {
    t4 = <div><select value={t0} aria-label="Category" onChange={t1} className="w-full border-b border-zinc-300 bg-transparent px-2 py-1 text-xs focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:focus:border-white" required={true}>{t2}{t3}</select></div>;
    $[4] = t0;
    $[5] = t1;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== modal) {
    t5 = modal.formData.category && modal.getSubcategoryOptions(modal.formData.category) && <div><select value={modal.formData.subcategory || ""} aria-label="Subcategory" onChange={e_0 => modal.setFormData(prev => ({
        ...prev,
        subcategory: e_0.target.value as ClothingSubcategory[keyof ClothingSubcategory]
      }))} className="w-full border-b border-zinc-300 bg-transparent px-2 py-1 text-xs focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:focus:border-white" required={true}><option value="">SELECT TYPE</option>{modal.getSubcategoryOptions(modal.formData.category).map(_temp2)}</select></div>;
    $[7] = modal;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function _temp2(subcategory) {
  return <option key={subcategory} value={subcategory}>{subcategory.toUpperCase().replace("_", " ")}</option>;
}
function _temp(t0) {
  const [key, config] = t0;
  return key === "underwear" || key === "fullbody" ? [] : [<option key={key} value={key}>{config.label.toUpperCase()}</option>];
}
function BrandSizeFields() {
  const $ = _c(13);
  const {
    formData,
    setFormData,
    sizeOptions
  } = useAddFromUrlModal();
  const t0 = formData.brand || "";
  let t1;
  if ($[0] !== setFormData) {
    t1 = e => setFormData(prev => ({
      ...prev,
      brand: e.target.value
    }));
    $[0] = setFormData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = <input type="text" placeholder="BRAND (OPTIONAL)" aria-label="Brand" value={t0} onChange={t1} className="border-b border-zinc-300 bg-transparent px-2 py-1 text-xs placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:placeholder:text-zinc-600 dark:focus:border-white" />;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== formData.category || $[6] !== formData.size || $[7] !== setFormData || $[8] !== sizeOptions) {
    t3 = formData.category && sizeOptions ? <select value={formData.size || ""} aria-label="Size" onChange={e_0 => setFormData(prev_0 => ({
      ...prev_0,
      size: e_0.target.value
    }))} className="border-b border-zinc-300 bg-transparent px-2 py-1 text-xs focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:focus:border-white"><option value="">SELECT SIZE</option>{sizeOptions.options.map(_temp3)}</select> : <input type="text" placeholder="SIZE (SELECT CATEGORY FIRST)" aria-label="Size" value={formData.size || ""} readOnly={true} disabled={true} className="border-b border-zinc-300 bg-transparent px-2 py-1 text-xs placeholder:text-zinc-400 disabled:opacity-50 dark:border-zinc-700 dark:placeholder:text-zinc-600" />;
    $[5] = formData.category;
    $[6] = formData.size;
    $[7] = setFormData;
    $[8] = sizeOptions;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <div className="grid grid-cols-2 gap-4">{t2}{t3}</div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function _temp3(option) {
  return <option key={option.value} value={option.value}>{option.label}</option>;
}
function ColorTagFields() {
  const $ = _c(13);
  const {
    formData,
    setFormData
  } = useAddFromUrlModal();
  const t0 = formData.color || "";
  let t1;
  if ($[0] !== setFormData) {
    t1 = e => setFormData(prev => ({
      ...prev,
      color: e.target.value
    }));
    $[0] = setFormData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = <div><input type="text" placeholder="COLORS (COMMA SEPARATED)" aria-label="Colors" value={t0} onChange={t1} className="w-full border-b border-zinc-300 bg-transparent px-2 py-1 text-xs placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:placeholder:text-zinc-600 dark:focus:border-white" /></div>;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = formData.tags || "";
  let t4;
  if ($[5] !== setFormData) {
    t4 = e_0 => setFormData(prev_0 => ({
      ...prev_0,
      tags: e_0.target.value
    }));
    $[5] = setFormData;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div><input type="text" placeholder="TAGS (COMMA SEPARATED)" aria-label="Tags" value={t3} onChange={t4} className="w-full border-b border-zinc-300 bg-transparent px-2 py-1 text-xs placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:placeholder:text-zinc-600 dark:focus:border-white" /></div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t5) {
    t6 = <>{t2}{t5}</>;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
function PriceSeasonFields() {
  const $ = _c(19);
  const {
    formData,
    setFormData
  } = useAddFromUrlModal();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="absolute top-1/2 left-0 -translate-y-1/2 text-xs text-zinc-600 dark:text-zinc-400">$</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = formData.price || "";
  let t2;
  if ($[1] !== setFormData) {
    t2 = e => setFormData(prev => ({
      ...prev,
      price: e.target.value
    }));
    $[1] = setFormData;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <div className="relative">{t0}<input type="number" step="0.01" placeholder="PRICE (OPTIONAL)" aria-label="Price" value={t1} onChange={t2} className="w-full border-b border-zinc-300 bg-transparent px-4 py-1 text-xs placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:placeholder:text-zinc-600 dark:focus:border-white" /></div>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = formData.season || "all";
  let t5;
  if ($[6] !== setFormData) {
    t5 = e_0 => setFormData(prev_0 => ({
      ...prev_0,
      season: e_0.target.value as Season
    }));
    $[6] = setFormData;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t10;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <option value="all">ALL SEASONS</option>;
    t7 = <option value="spring">SPRING</option>;
    t8 = <option value="summer">SUMMER</option>;
    t9 = <option value="fall">FALL</option>;
    t10 = <option value="winter">WINTER</option>;
    $[8] = t10;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t10 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t11;
  if ($[13] !== t4 || $[14] !== t5) {
    t11 = <select value={t4} aria-label="Season" onChange={t5} className="border-b border-zinc-300 bg-transparent px-2 py-1 text-xs focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:focus:border-white">{t6}{t7}{t8}{t9}{t10}</select>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t3) {
    t12 = <div className="grid grid-cols-2 gap-4">{t3}{t11}</div>;
    $[16] = t11;
    $[17] = t3;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
}
function SubmitActions() {
  const $ = _c(8);
  const {
    loading,
    processingUpload,
    onClose
  } = useAddFromUrlModal();
  const t0 = loading ? processingUpload ? "REMOVING BACKGROUND..." : "ADDING..." : "ADD ITEM";
  let t1;
  if ($[0] !== loading || $[1] !== t0) {
    t1 = <button type="submit" disabled={loading} className="flex-1 border border-zinc-900 px-4 py-2 text-xs transition-colors hover:bg-zinc-900 hover:text-white disabled:opacity-50 dark:border-white dark:hover:bg-white dark:hover:text-zinc-900">{t0}</button>;
    $[0] = loading;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== onClose) {
    t2 = <button type="button" onClick={onClose} className="flex-1 border border-zinc-300 px-4 py-2 text-xs transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800">CANCEL</button>;
    $[3] = onClose;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex gap-4 pt-4">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}